import { useEffect, useRef, useState } from "react";

/* On-device face presence for proctoring — Google MediaPipe Face Detector
   (Apache-2.0), WASM + model self-hosted under /public/mediapipe. Nothing
   leaves the browser; we only count whether a face is in frame.

   useFaceGuard(videoRef, enabled, tracking)
     enabled  → run the detector (camera is on)
     tracking → accumulate absence time (session is live)
   returns { supported, ready, present, faces, absentSec, maxFaces, checks, reset, snapshot() } */

const SAMPLE_MS = 700;
// Debug hook (read-only) so proctoring can be inspected in devtools / e2e tests.
const dbg = (typeof window !== "undefined") ? (window.__faceGuard = window.__faceGuard || {}) : {};
let detectorPromise = null;

async function loadDetector() {
  if (!detectorPromise) {
    detectorPromise = (async () => {
      const { FilesetResolver, FaceDetector } = await import("@mediapipe/tasks-vision");
      const vision = await FilesetResolver.forVisionTasks("/mediapipe/wasm");
      return FaceDetector.createFromOptions(vision, {
        baseOptions: { modelAssetPath: "/mediapipe/blaze_face_short_range.tflite", delegate: "GPU" },
        runningMode: "VIDEO",
        minDetectionConfidence: 0.5,
      }).catch(() => FaceDetector.createFromOptions(vision, {
        baseOptions: { modelAssetPath: "/mediapipe/blaze_face_short_range.tflite", delegate: "CPU" },
        runningMode: "VIDEO",
        minDetectionConfidence: 0.5,
      }));
    })().catch((e) => { detectorPromise = null; throw e; });
  }
  return detectorPromise;
}

export function useFaceGuard(videoRef, enabled, tracking = false) {
  const [state, setState] = useState({ supported: true, ready: false, present: false, faces: 0 });
  const acc = useRef({ absentSec: 0, maxFaces: 0, checks: 0 });
  const trackingRef = useRef(tracking);
  useEffect(() => { trackingRef.current = tracking; }, [tracking]);

  useEffect(() => {
    if (!enabled) { setState((s) => ({ ...s, present: false, faces: 0 })); return; }
    let det = null, timer = null, stopped = false, lastTs = 0;
    (async () => {
      try { det = await loadDetector(); } catch (e) { console.warn("Face detector unavailable", e); dbg.error = String(e && e.message || e); if (!stopped) setState((s) => ({ ...s, supported: false, ready: false })); return; }
      dbg.loaded = true;
      if (stopped) return;
      setState((s) => ({ ...s, ready: true }));
      timer = setInterval(() => {
        const v = videoRef.current;
        dbg.video = v ? { readyState: v.readyState, w: v.videoWidth, h: v.videoHeight } : null;
        if (!v || v.readyState < 2 || v.videoWidth === 0) return;
        let ts = performance.now(); if (ts <= lastTs) ts = lastTs + 1; lastTs = ts;
        let n = 0;
        try { n = (det.detectForVideo(v, ts).detections || []).length; } catch (e) { dbg.detectError = String(e && e.message || e); return; }
        dbg.lastFaces = n; dbg.samples = (dbg.samples || 0) + 1;
        if (trackingRef.current) {
          acc.current.checks += 1;
          if (n === 0) acc.current.absentSec += SAMPLE_MS / 1000;
          if (n > acc.current.maxFaces) acc.current.maxFaces = n;
        }
        setState((s) => (s.present === (n > 0) && s.faces === n ? s : { ...s, present: n > 0, faces: n }));
      }, SAMPLE_MS);
    })();
    return () => { stopped = true; if (timer) clearInterval(timer); };
  }, [enabled, videoRef]);

  const reset = () => { acc.current = { absentSec: 0, maxFaces: 0, checks: 0 }; };
  const snapshot = () => ({ face_absent_sec: Math.round(acc.current.absentSec * 10) / 10, face_checks: acc.current.checks, max_faces: Math.max(1, acc.current.maxFaces) });
  return { ...state, absentSec: acc.current.absentSec, maxFaces: acc.current.maxFaces, checks: acc.current.checks, reset, snapshot };
}

/* Wait until a face is in frame (for the pre-start check). Resolves true/false. */
export function waitForFace(getPresent, timeoutMs = 8000) {
  return new Promise((resolve) => {
    const start = Date.now();
    const t = setInterval(() => {
      if (getPresent()) { clearInterval(t); resolve(true); }
      else if (Date.now() - start > timeoutMs) { clearInterval(t); resolve(false); }
    }, 200);
  });
}
